import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const ProjectHeader = ({ project, onEditProject }) => {
  const [isCoverOpen, setIsCoverOpen] = useState(false);

  const coverSrc = useMemo(
    () => project?.coverImage || '/assets/images/no_image.png',
    [project?.coverImage]
  );
  const coverAlt = useMemo(
    () => project?.coverImageAlt || project?.title || 'Project cover image',
    [project?.coverImageAlt, project?.title]
  );

  const completionPercentage = project?.totalChallenges > 0 
    ? Math.round((project?.completedChallenges / project?.totalChallenges) * 100) 
    : 0;

  useEffect(() => {
    if (!isCoverOpen) return;

    const onKeyDown = (e) => {
      if (e?.key === 'Escape') {
        setIsCoverOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isCoverOpen]);

  return (
    <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6 lg:p-8 mb-6 md:mb-8">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 lg:gap-6 mb-6">
        <div className="flex items-start gap-4 lg:gap-5 flex-1 min-w-0">
          <button
            type="button"
            className="w-20 h-20 md:w-24 md:h-24 rounded-md overflow-hidden bg-muted border border-border flex-shrink-0 cursor-zoom-in"
            onClick={() => setIsCoverOpen(true)}
            aria-label="목표 이미지 크게 보기"
          >
            <Image src={coverSrc} alt={coverAlt} className="w-full h-full object-cover" />
          </button>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground mb-2 md:mb-3">
              {project?.title}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground line-clamp-2">
              {project?.description}
            </p>
          </div>
        </div>
        
        {onEditProject && (
          <button
            onClick={onEditProject}
            className="flex items-center space-x-2 px-4 md:px-6 py-2 md:py-3 bg-muted hover:bg-muted/80 text-foreground rounded-md transition-gaming self-start lg:self-auto"
          >
            <Icon name="Edit" size={18} />
            <span className="text-sm md:text-base font-medium">목표 편집</span>
          </button>
        )}
      </div>

      {isCoverOpen && (
        <div
          className="fixed inset-0 z-dialog bg-background/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setIsCoverOpen(false)}
        >
          <div
            className="w-full max-w-5xl bg-card rounded-lg border border-border shadow-gaming-lg overflow-hidden"
            onClick={(e) => e?.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div className="text-base md:text-lg font-heading font-semibold text-foreground">
                목표 이미지
              </div>
              <button
                type="button"
                className="px-3 py-2 rounded-md hover:bg-muted transition-gaming text-sm"
                onClick={() => setIsCoverOpen(false)}
              >
                닫기
              </button>
            </div>
            <div className="p-4 bg-background">
              <div className="w-full max-h-[75vh] flex items-center justify-center">
                <Image
                  src={coverSrc}
                  alt={coverAlt}
                  className="max-h-[75vh] w-auto max-w-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6">
        <div className="bg-background rounded-md p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center">
              <Icon name="Target" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <div className="text-xs md:text-sm text-muted-foreground caption">총 챌린지</div>
              <div className="text-lg md:text-xl font-bold text-foreground data-text">
                {project?.totalChallenges}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-md p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-success/20 rounded-md flex items-center justify-center">
              <Icon name="CheckCircle" size={20} color="var(--color-success)" />
            </div>
            <div>
              <div className="text-xs md:text-sm text-muted-foreground caption">완료된 챌린지</div>
              <div className="text-lg md:text-xl font-bold text-foreground data-text">
                {project?.completedChallenges}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-md p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-accent/20 rounded-md flex items-center justify-center">
              <Icon name="Image" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <div className="text-xs md:text-sm text-muted-foreground caption">총 인증</div>
              <div className="text-lg md:text-xl font-bold text-foreground data-text">
                {project?.totalProofs}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-background rounded-md p-4">
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 bg-primary/20 rounded-md flex items-center justify-center">
              <Icon name="Star" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <div className="text-xs md:text-sm text-muted-foreground caption">평균 별점</div>
              <div className="text-lg md:text-xl font-bold text-foreground data-text">
                {project?.averageStars?.toFixed(1)}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm md:text-base font-medium text-foreground">전체 진행률</span>
          <span className="text-sm md:text-base font-bold text-primary data-text">
            {completionPercentage}%
          </span>
        </div>
        <div className="w-full h-3 md:h-4 bg-muted rounded-full overflow-hidden">
          <div
            className="h-full bg-primary transition-gaming rounded-full"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectHeader;